import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import RuleIcon from '@mui/icons-material/Rule';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import HistoryIcon from '@mui/icons-material/History';
import DescriptionIcon from '@mui/icons-material/Description';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Badge } from '@mui/material';
import { hasUnseenRelease } from './ReleaseNotes';

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 68;

interface SidebarProps {
  currentView: string;
  onViewChange: (view: string) => void;
  isAdmin?: boolean;
}

interface SubItem {
  id: string;
  label: string;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
  subItems?: SubItem[];
}

const navItems: NavItem[] = [
  { id: 'my-rules', label: 'Rules', icon: <RuleIcon /> },
  { id: 'approvals', label: 'Approvals', icon: <AssignmentTurnedInIcon />, adminOnly: true },
  { id: 'audit-log', label: 'Audit Log', icon: <HistoryIcon />, adminOnly: true },
  {
    id: 'docs',
    label: 'Docs',
    icon: <DescriptionIcon />,
    subItems: [
      { id: 'docs:how-to-create', label: 'How to Create a Rule' },
      { id: 'docs:rule-instructions', label: 'Rule Instructions & Features' },
      { id: 'docs:bulk-upload', label: 'Bulk Rule Creation' },
      { id: 'docs:adobe-analytics', label: 'How to Request Access' },
    ],
  },
  { id: 'faqs', label: 'FAQs', icon: <HelpOutlineIcon /> },
  { id: 'release-notes', label: 'Release Notes', icon: <NewReleasesIcon /> },
];

const Sidebar: React.FC<SidebarProps> = ({ currentView, onViewChange, isAdmin = false }) => {
  const [expanded, setExpanded] = useState(true);
  const [docsOpen, setDocsOpen] = useState(false);

  const width = expanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH;

  const visibleItems = navItems.filter(item => !item.adminOnly || isAdmin);

  return (
    <Box
      sx={{
        width,
        minWidth: width,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
        borderRight: '1px solid #e0e0e0',
        transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'visible',
        position: 'relative',
      }}
    >
      {/* Title header */}
      <Box
        sx={{
          px: expanded ? 2.5 : 0,
          pt: 2.5,
          pb: 2,
          minHeight: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: expanded ? 'flex-start' : 'center',
          overflow: 'hidden',
          transition: 'padding 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {expanded ? (
          <Box
            onClick={() => onViewChange('home')}
            sx={{ cursor: 'pointer' }}
          >
            <Typography
              sx={{
                color: '#000',
                fontWeight: 800,
                fontSize: '26px',
                fontFamily: "'Inter', 'Segoe UI', 'Roboto', sans-serif",
                letterSpacing: '-0.5px',
                lineHeight: 1.2,
              }}
            >
              QR Redirect
            </Typography>
          </Box>
        ) : (
          <Tooltip title="QR Redirect" placement="right" arrow>
            <Box
              onClick={() => onViewChange('home')}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <MenuIcon sx={{ fontSize: 28, color: '#000' }} />
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Collapse/Expand toggle - at intersection of right border */}
      <IconButton
        onClick={() => setExpanded(!expanded)}
        sx={{
          position: 'absolute',
          top: 52,
          right: -16,
          bgcolor: '#1a1a2e',
          color: '#fff',
          width: 32,
          height: 32,
          borderRadius: '8px',
          border: '2px solid #2c2c44',
          zIndex: 2,
          '&:hover': { bgcolor: '#2c2c44' },
        }}
      >
        {expanded ? (
          <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 18 }} />
        ) : (
          <KeyboardDoubleArrowRightIcon sx={{ fontSize: 18 }} />
        )}
      </IconButton>

      {/* Navigation items */}
      <Box sx={{ flex: 1, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.3, overflow: 'hidden' }}>
        {visibleItems.map((item) => {
          const hasSubItems = item.subItems && item.subItems.length > 0;
          const isActive = hasSubItems
            ? false
            : currentView === item.id;

          const handleClick = () => {
            if (hasSubItems) {
              if (!expanded) {
                setExpanded(true);
                setDocsOpen(true);
              } else {
                setDocsOpen(!docsOpen);
              }
            } else {
              setDocsOpen(false);
              onViewChange(item.id);
            }
          };

          const navButton = (
            <Box
              key={item.id}
              onClick={handleClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: expanded ? 2.5 : 0,
                py: 1.2,
                mx: expanded ? 1 : 0.8,
                borderRadius: '8px',
                cursor: 'pointer',
                justifyContent: expanded ? 'flex-start' : 'center',
                bgcolor: isActive ? 'rgba(33, 150, 243, 0.08)' : 'transparent',
                borderLeft: isActive ? '3px solid #2196F3' : '3px solid transparent',
                transition: 'all 0.15s ease',
                '&:hover': {
                  bgcolor: isActive ? 'rgba(33, 150, 243, 0.12)' : 'rgba(0,0,0,0.04)',
                },
              }}
            >
              <Box
                sx={{
                  color: isActive ? '#2196F3' : '#000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 24,
                  '& .MuiSvgIcon-root': { fontSize: 22 },
                }}
              >
                {item.id === 'release-notes' ? (
                  <Badge
                    variant="dot"
                    invisible={!hasUnseenRelease()}
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: '#f44336',
                        width: 8,
                        height: 8,
                        minWidth: 8,
                        borderRadius: '50%',
                      }
                    }}
                  >
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </Box>
              {expanded && (
                <>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? '#2196F3' : '#000',
                      fontFamily: "'Inter', 'Segoe UI', 'Roboto', sans-serif",
                      whiteSpace: 'nowrap',
                      flex: 1,
                    }}
                  >
                    {item.label}
                  </Typography>
                  {hasSubItems && (
                    <ExpandMoreIcon
                      sx={{
                        fontSize: 20,
                        color: '#666',
                        transition: 'transform 0.25s ease',
                        transform: docsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    />
                  )}
                </>
              )}
            </Box>
          );

          if (!expanded) {
            return (
              <Tooltip key={item.id} title={item.label} placement="right" arrow>
                {navButton}
              </Tooltip>
            );
          }

          return (
            <Box key={item.id}>
              {navButton}
              {hasSubItems && expanded && (
                <Collapse in={docsOpen} timeout={200}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                    {item.subItems!.map((sub) => {
                      const isSubActive = currentView === sub.id;
                      return (
                        <Box
                          key={sub.id}
                          onClick={() => onViewChange(sub.id)}
                          sx={{
                            pl: 5.5,
                            pr: 2,
                            py: 1,
                            mx: 1,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            bgcolor: isSubActive ? 'rgba(33, 150, 243, 0.08)' : 'transparent',
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              bgcolor: isSubActive ? 'rgba(33, 150, 243, 0.12)' : 'rgba(0,0,0,0.04)',
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: '15px',
                              fontWeight: isSubActive ? 600 : 400,
                              color: isSubActive ? '#2196F3' : '#1a1a1a',
                              fontFamily: "'Inter', 'Segoe UI', 'Roboto', sans-serif",
                              lineHeight: 1.4,
                            }}
                          >
                            {sub.label}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Collapse>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Left accent line */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '3px',
          bgcolor: '#2196F3',
        }}
      />
    </Box>
  );
};

export default Sidebar;
